import { Directive, Input } from '@angular/core';
import { AbstractControl } from '@angular/forms';

import { ProtectedControlsContext } from './protected-controls.context';
import { ProtectedControlsService } from './protected-controls.service';

export interface ProtectedControlMeta {
  context: ProtectedControlsContext;
  message?: string;
  name?: string;
  newValue?: any;
  oldValue?: any;
}

export interface ProtectedControlOptions {
  /**
    @desc Array of property getters on AbstractControl: https://angular.io/api/forms/AbstractControl
    Used for producing "locked" state (= control[bailOnProp[0]] && control[bailOnProp[1]] && control[bailOnProp[2]] ...)

    Valid props are: ['dirty', 'valid', 'invalid', 'pending', 'enabled', 'pristine', 'touched', 'untouched']
  */

  bailOn: (
    | 'dirty'
    | 'valid'
    | 'invalid'
    | 'pending'
    | 'enabled'
    | 'pristine'
    | 'touched'
    | 'untouched'
  )[];
  bailOnlyIfChanged: boolean;
}

@Directive({
  selector: '[rtProtectedControl]',
  exportAs: 'rtProtectedControl',
})
export class ProtectedControlDirective {
  @Input('rtProtectedControlName') public name: string = 'unknown';
  @Input('rtProtectedControlOptions')
  public options: ProtectedControlOptions = {
    bailOn: ['dirty'],
    bailOnlyIfChanged: false,
  };
  @Input('rtProtectedControlMessage') public message: string;
  @Input('rtProtectedControl')
  public set control(control: AbstractControl | null) {
    if (control) {
      this._initialValue = control.value;
      this._control = control;
    }
  }

  public get control(): AbstractControl | null {
    return this._control;
  }

  public get meta(): ProtectedControlMeta {
    return {
      context: this.context,
      message: this.message,
      name: this.name,
      newValue: this.control?.value,
      oldValue: this._initialValue,
    };
  }

  private _control: AbstractControl;
  private _initialValue: null;

  constructor(private srvc: ProtectedControlsService, private context: ProtectedControlsContext) {}

  public ngOnInit(): void {
    this.srvc.register(this);
  }

  public ngOnDestroy(): void {
    this.srvc.unregister(this);
  }

  public get locked(): boolean {
    if (!this.control) {
      return false;
    }
    return this.isControlLocked();
  }

  private isControlLocked(): boolean {
    if (this.options.bailOnlyIfChanged) {
      if (this._initialValue === this.control!.value) {
        return false;
      }
    }

    return this.options.bailOn.some(opt => this.control![opt]);
  }
}
